import React from 'react';
import { Popover } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import { FormatCurrency } from 'shared/Helpers';
import { CalendarProposal } from 'models/Renewals';

interface StatusPalette {
  bg: string;
  fg: string;
  border: string;
}

/**
 * Status-pill palette keyed by the proposal's display status (case-insensitive).
 * Mirrors the design handoff; anything unmatched falls back to the Draft palette.
 */
const STATUS_PALETTE: Record<string, StatusPalette> = {
  received: { bg: '#EAF4FF', fg: '#1565C0', border: '#BBDDFB' },
  submitted: { bg: '#FFF3E0', fg: '#B26A00', border: '#FAD8A0' },
  approved: { bg: '#E9F8EC', fg: '#2E7D32', border: '#BDE9C5' },
  'in review': { bg: '#F3EEFF', fg: '#6D28D9', border: '#D9CCF7' },
  'pending review': { bg: '#F3EEFF', fg: '#6D28D9', border: '#D9CCF7' },
  declined: { bg: '#FDECEC', fg: '#C62828', border: '#F6C9C9' },
  draft: { bg: '#F1F3F5', fg: '#555555', border: '#DDE1E6' }
};

const DRAFT_PALETTE = STATUS_PALETTE.draft;

/**
 * Resolves the pill palette for a proposal status label.
 * @param {string | null} status - The proposal's display status.
 * @returns {StatusPalette} The matching palette (Draft when unmatched/empty).
 */
const getStatusPalette = (status: string | null): StatusPalette => {
  if (!status) return DRAFT_PALETTE;
  return STATUS_PALETTE[status.trim().toLowerCase()] ?? DRAFT_PALETTE;
};

/**
 * Formats an ISO date string to the locale short date, with an em-dash fallback.
 * @param {string | null} value - The ISO date string.
 * @returns {string} The formatted date or an em-dash.
 */
const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.format('l') : '—';
};

/**
 * Renders a compact status pill for a proposal.
 * @param {object} props - Component props.
 * @param {string | null} props.status - The proposal status label.
 * @returns {React.ReactElement} The status pill.
 */
const StatusPill = ({ status }: { status: string | null }): React.ReactElement => {
  const palette = getStatusPalette(status);
  return (
    <span
      className="inline-block rounded-[4px] border font-semibold whitespace-nowrap text-[11px] px-[9px] py-[2px]"
      style={{ background: palette.bg, color: palette.fg, borderColor: palette.border }}
    >
      {status || 'Draft'}
    </span>
  );
};

interface RenewalProposalsPopoverProps {
  proposals: CalendarProposal[];
  title: string;
}

/**
 * Proposals count badge with a hover popover summary (Smart Calendar™).
 *
 * Renders the count pill used in the Gantt `col-proposals` cell; hovering opens a
 * 720px antd popover listing every proposal as a compact one-line-per-proposal summary.
 * @param {RenewalProposalsPopoverProps} props - Component props.
 * @param {CalendarProposal[]} props.proposals - The proposals attached to the row.
 * @param {string} props.title - The deal/contract name shown in the popover header.
 * @returns {React.ReactElement} The badge + popover.
 */
const RenewalProposalsPopover = ({ proposals, title }: RenewalProposalsPopoverProps): React.ReactElement => {
  const isMulti = proposals.length > 1;

  const content = (
    <div className="w-[720px]">
      <div className="flex items-center gap-[11px] px-[18px] py-[14px] bg-[#2A2150]">
        <AppstoreOutlined className="text-white text-[18px] shrink-0" />
        <span className="text-[17px] font-bold text-white">Proposals</span>
        <span className="text-[13px] font-medium text-white/65 truncate max-w-[280px]">— {title}</span>
      </div>
      <div className="max-h-[300px] overflow-y-auto py-[6px]">
        {proposals.map((p, index) => (
          <div
            key={p.id || index}
            className="flex items-center gap-3 px-[18px] py-3 border-b border-[#f3f3f6] hover:bg-[#fafafa]"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-[9px]">
                <span className="font-semibold text-sm text-[#1A1A1A]">{p.subject || 'Proposal'}</span>
                <StatusPill status={p.status} />
              </div>
              <div className="text-xs text-[#888] mt-[3px]">
                {[p.supplier, p.term, p.proposalDate ? `proposed ${formatDate(p.proposalDate)}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-base font-bold text-[#1A1A1A]">{FormatCurrency(p.tcv, 0)}</div>
              <div className="text-[11px] text-[#888] mt-[1px]">exp {formatDate(p.expiration)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      placement="top"
      trigger="hover"
      mouseEnterDelay={0.1}
      mouseLeaveDelay={0.14}
      overlayClassName="[&_.ant-popover-inner-content]:!p-0 [&_.ant-popover-inner]:!rounded-lg [&_.ant-popover-inner]:!overflow-hidden"
    >
      <span
        className={`inline-flex items-center justify-center min-w-[30px] h-[30px] px-[9px] rounded-full text-[13px] font-bold cursor-pointer transition-transform duration-[120ms] hover:scale-[1.12] hover:shadow-[0_4px_12px_rgba(21,45,81,0.28)] ${
          isMulti ? 'bg-[#152D51] text-white' : 'bg-[#E7EBF0] text-[#555]'
        }`}
      >
        {proposals.length}
      </span>
    </Popover>
  );
};

export default RenewalProposalsPopover;
